import "../styles/globals.css";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useEffect, useMemo, useState } from "react";
import { Router } from "next/router";
import Script from "next/script";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

const AppDotsLoading = dynamic(() => import("../components/AppDotsLoading"), {
  loading: () => <p>Loading...</p>,
});
const AppPopup = dynamic(() => import("../components/AppPopup"), {
  loading: () => <p>Loading...</p>,
});

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const siteUrl =
    process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/+$/, "") || "https://gromo.in";
  const isStaging =
    siteUrl.includes("stg") ||
    siteUrl.includes("staging") ||
    process.env.NEXT_PUBLIC_ENV === "stg" ||
    process.env.NEXT_PUBLIC_ENV === "staging";
  const canonicalPathRaw = (router.asPath || "/").split("?")[0].split("#")[0];
  const canonicalPath =
    canonicalPathRaw !== "/" ? canonicalPathRaw.replace(/\/+$/, "") : "/";
  const canonicalUrl = `${siteUrl}${canonicalPath.startsWith("/") ? "" : "/"}${canonicalPath}`;
  const isHome = canonicalPath === "/";

  const hrefLangLinks = useMemo(() => {
    const locales = router.locales || [];
    const defaultLocale = router.defaultLocale || "en";
    const currentLocale = router.locale || defaultLocale;
    const basePath = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;

    const normalizedPath =
      currentLocale !== defaultLocale && basePath.startsWith(`/${currentLocale}`)
        ? basePath.replace(`/${currentLocale}`, "") || "/"
        : basePath;

    const links = locales.map((locale) => {
      const localePath =
        locale === defaultLocale
          ? normalizedPath
          : `/${locale}${normalizedPath === "/" ? "" : normalizedPath}`;
      return {
        locale,
        href: `${siteUrl}${localePath}`,
      };
    });

    const defaultHref =
      links.find((link) => link.locale === defaultLocale)?.href || canonicalUrl;

    return {
      links,
      defaultHref,
    };
  }, [router.locales, router.defaultLocale, router.locale, canonicalPath, canonicalUrl, siteUrl]);

  const organizationId = `${siteUrl}/#organization`;
  const websiteId = `${siteUrl}/#website`;

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": organizationId,
    name: "GroMo",
    alternateName: "GroMo Fintech",
    legalName: "Vitrak Technologies Private Limited",
    url: siteUrl,
    logo: `${siteUrl}/assets/images/home/GromoLogo.svg`,
    description:
      "India's largest financial product distribution platform where anyone can earn by selling credit cards, loans & more with zero investment",
    foundingDate: "2019",
    founders: [
      { "@type": "Person", name: "Ankit Khandelwal" },
      { "@type": "Person", name: "Darpan Khurana" },
    ],
    founder: [
      { "@type": "Person", name: "Ankit Khandelwal" },
      { "@type": "Person", name: "Darpan Khurana" },
    ],
    areaServed: "IN",
    address: {
      "@type": "PostalAddress",
      streetAddress: "3rd floor, Plot no. 130, Sector 44",
      addressLocality: "Gurugram",
      addressRegion: "Haryana",
      postalCode: "122003",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 28.4507064,
      longitude: 77.0738281,
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+91-8384030103",
        contactType: "customer support",
        email: "contact@gromo.in",
        areaServed: "IN",
        availableLanguage: ["English", "Hindi"],
      },
    ],
    sameAs: [
      "https://www.linkedin.com/company/gromofintech/",
      "https://www.youtube.com/channel/UCn5RV1XAm2Fpq5CRn_aw21A?sub_confirmation=1",
      "https://www.instagram.com/gromo_official.in?igsh=MWpxaXR6ZGkxemtmbA==",
      "https://www.facebook.com/gromofintech/",
    ],
  };

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": websiteId,
    url: siteUrl,
    name: "GroMo",
    publisher: {
      "@id": organizationId,
    },
    inLanguage: "en-IN",
  };

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${siteUrl}/#service`,
    name: "GroMo financial product distribution platform",
    provider: {
      "@id": organizationId,
    },
    areaServed: {
      "@type": "Country",
      name: "IN",
    },
  };

  const softwareApplicationJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "GroMo",
    operatingSystem: "Android",
    applicationCategory: "FinanceApplication",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.3",
      reviewCount: "68000",
    },
  };

  useEffect(() => {
    const handleStart = () => setLoading(true);
    const handleComplete = () => setLoading(false);

    Router.events.on("routeChangeStart", handleStart);
    Router.events.on("routeChangeComplete", handleComplete);
    Router.events.on("routeChangeError", handleComplete);

    return () => {
      Router.events.off("routeChangeStart", handleStart);
      Router.events.off("routeChangeComplete", handleComplete);
      Router.events.off("routeChangeError", handleComplete);
    };
  }, []);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        {hrefLangLinks.links.map((link) => (
          <link
            rel="alternate"
            hrefLang={link.locale === "en" ? "en-IN" : link.locale}
            href={link.href}
            key={`hreflang-${link.locale}`}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href={hrefLangLinks.defaultHref}
          key="hreflang-x-default"
        />
        <meta name="geo.region" content="IN" key="geo.region" />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
          key="jsonld-organization"
        />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }}
          key="jsonld-website"
        />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }}
          key="jsonld-service"
        />
        {isHome && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(softwareApplicationJsonLd),
            }}
            key="jsonld-software-application"
          />
        )}
        {isStaging && <meta name="robots" content="noindex, nofollow" />}
      </Head>

      {loading && <AppDotsLoading />}
      <Script
        async
        src="https://www.googletagmanager.com/gtag/js?id=G-LZX2HCL5C3"
      />
      <AppPopup />
      <Component {...pageProps} />
    </>
  );
}
